import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import { task, type TaskInstance } from 'ember-concurrency';
import type { ParityInsightHistoricalData } from 'lumen/models';
import type { BreakdownItemsPerExtractDate } from 'lumen/models/parity/insight/historical-data';
import type { RouteQueryParams as HotelParityInsightParams } from 'lumen/routes/market/parity/insight';
import type {
  RouteQueryParams,
  TrendsDataRequestParams,
} from 'lumen/routes/market/parity/insight/trends';
import type { Transition } from 'lumen/services/transition';
import {
  APIGATEWAY,
  MEMBERSHIPTYPES,
} from 'lumen/utils/constants/general-constants';
import { HISTORICAL_POINTS_AMOUNT } from 'lumen/utils/constants/pi-constant-values';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

export default class HotelParityInsightTrendsRoute extends Route {
  queryParams = {
    segmentation: {
      refreshModel: true,
    },
    selection: {
      refreshModel: false,
    },
    trendsExtractDate: {
      refreshModel: true,
    },
  };

  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  fetchTrendsDataTask = task(async (params: RouteQueryParams) => {
    try {
      const {
        dateAggregation,
        extractDate,
        segmentationFilters,
        los,
        mealPlan,
        pos,
        rateType,
        showMemberRate,
      } = this.paramsFor('hotel.parity.insight') as HotelParityInsightParams;

      const requestParams: TrendsDataRequestParams = {
        'aggregations[]': params.segmentation,
        extract_date: params.trendsExtractDate ?? extractDate,
        date_aggregation: dateAggregation,
        periods: HISTORICAL_POINTS_AMOUNT,
        los,
        mealtype: mealPlan,
        pos,
        rate_type: rateType,
        subscription_id: this.hotelAndMarketService.hotelOrMarket.id,
      };

      if (segmentationFilters.length) {
        requestParams.filters = segmentationFilters;
      }

      if (showMemberRate) {
        requestParams.base_membership_type = MEMBERSHIPTYPES.MEMBER_ONLY;
      }

      const trendsData =
        (await this.store.queryRecord<ParityInsightHistoricalData>(
          'parity/insight/historical-data',
          {
            namespace: APIGATEWAY,
            typeKey: 'parityanalytics/api/single_property',
            ...requestParams,
          },
        ))!;

      return trendsData.getBreakdownData(params.segmentation);
    } catch (error) {
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }
  });

  beforeModel(transition: Transition): void {
    this.transition.validateParityInsightTrendsQueryParams(transition);
  }

  model(params: RouteQueryParams): {
    trendsData: TaskInstance<BreakdownItemsPerExtractDate>;
  } {
    return {
      trendsData: this.fetchTrendsDataTask.perform(params),
    };
  }
}
